// Editorial-question API client. Two implementations:
//
//   editorialQuestionMockClient — json-server-backed (used by `yarn mock:api`
//     on :3032). The default during local dev.
//
//   editorialQuestionHenryClient — talks to Henry's api_v2/item/main_resource/*
//     surface via the adapter in main_resource.ts. Activated by setting the
//     env var REACT_APP_EDITORIAL_QUESTION_CLIENT='henry'.
//
// This file is the translator between the panel's editorial-question model
// (string ids, sectionNotes, status names, …) and Henry's resource_item +
// folded-answers model (number ids, section_meta, editorial_status, …).
//
// Companion docs: vault/AIQBP - QB Platform/Henry v2 - Required Backend
// Amendments.md (Henry-facing handoff).

import {
  EditorialQuestion,
  EditorialQuestionInput,
  EditorialQuestionListParams,
  EditorialQuestionListResult,
  EditorialQuestionOption,
  EditorialQuestionStatus,
  EditorialQuestionTemplate,
  T6AudioWordItem,
  editorialQuestionClient as editorialQuestionMockClient,
  isChineseWordBankTemplate
} from './editorialQuestion.mock';
import * as mainResourceClient from './main_resource';
import { chineseDictionaryClient } from './chineseDictionary';
import { normalizeSubjectName } from './subjectNames';
import {
  AddResourceResponse,
  ChineseWordAudio,
  EDITORIAL_TRANSITIONS,
  EditorialListFilters,
  EditorialListRow,
  EditorialStatus,
  FlatTopicRow,
  FullInfoAnswer,
  FullInfoResponse,
  MainResourceError,
  MainResourceResultCode,
  SaveAllAnswer,
  SaveAllPayload
} from './main_resource.types';

export type {
  EditorialQuestion,
  EditorialQuestionInput,
  EditorialQuestionListParams,
  EditorialQuestionListResult,
  EditorialQuestionOption,
  EditorialQuestionSectionNotes,
  EditorialQuestionStatus,
  EditorialQuestionTemplate,
  T6AudioWordItem
} from './editorialQuestion.mock';
export { CHINESE_WORD_BANK_TEMPLATES, isChineseWordBankTemplate } from './editorialQuestion.mock';

type SaveOptions = {
  transitionTo?: EditorialQuestionStatus;
};

// ── Field mappers (panel ↔ Henry) ────────────────────────────────────────

const TEMPLATE_TO_TYPE_ID: Record<
  EditorialQuestionTemplate,
  1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11
> = {
  T1: 1,
  T2: 2,
  T3: 3,
  T4: 4,
  T5: 5,
  T6: 6,
  T7: 7,
  T8: 8,
  T9: 9,
  T10: 10,
  T11: 11
};

function templateFromTypeId(id: number | string): EditorialQuestionTemplate {
  const num = typeof id === 'string' ? Number(id) : id;
  switch (num) {
    case 2:
      return 'T2';
    case 3:
      return 'T3';
    case 4:
      return 'T4';
    case 5:
      return 'T5';
    case 6:
      return 'T6';
    case 7:
      return 'T7';
    case 8:
      return 'T8';
    case 9:
      return 'T9';
    case 10:
      return 'T10';
    case 11:
      return 'T11';
    case 1:
    default:
      return 'T1';
  }
}

function levelToTargetGrade(level: string): 'k1' | 'k2' | 'k3' {
  const lc = String(level || '').toLowerCase();
  return lc === 'k1' || lc === 'k3' ? lc : 'k2';
}

function targetGradeToLevel(grade: string | undefined): string {
  const lc = String(grade || '').toLowerCase();
  return lc ? lc.toUpperCase() : 'K2';
}

const VALID_STATUSES: EditorialStatus[] = [
  'draft',
  'asset_ready',
  'approved',
  'urgent',
  'archived'
];

function toEditorialStatus(value: unknown): EditorialStatus {
  const text = String(value || '').toLowerCase() as EditorialStatus;
  return VALID_STATUSES.includes(text) ? text : 'draft';
}

const OPTION_KEYS = ['a', 'b', 'c', 'd'];

// Resolve Henry's resource_mc_answer_item.id for an option. Prefer the id
// carried on the option itself (fromFullInfo sets option.answerId), so
// delete/reorder maps to the correct answer row and orphans only the option
// the user actually removed. Fall back to positional answer_ids only when the
// option has no answerId yet (e.g. a brand-new draft whose options were never
// hydrated from get_full_info). A returned undefined means "create a new
// answer row" server-side.
function resolveAnswerId(option: EditorialQuestionOption, answerIds: number[] | undefined) {
  if (typeof option.answerId === 'number' && option.answerId > 0) {
    return option.answerId;
  }
  if (!answerIds || answerIds.length === 0) {
    return undefined;
  }
  const idx = OPTION_KEYS.indexOf(option.id);
  return idx >= 0 ? answerIds[idx] : undefined;
}

function resourceTypeForQuestion(input: EditorialQuestionInput): SaveAllPayload['type'] {
  // Click-time createDraft is always T1/mc_math. After the editor switches to
  // T7/T8, save_all must flip type to mc_chinese even if subject is still math.
  // UI labels (中文) and API keys (Chinese) also flip mc_math → mc_chinese.
  return isChineseWordBankTemplate(input.template) ||
    normalizeSubjectName(input.subject || '') === 'Chinese'
    ? 'mc_chinese'
    : 'mc_math';
}

function normalizeTopicSubject(subject: string | null | undefined) {
  const text = String(subject || '').trim();
  return text ? normalizeSubjectName(text) : text;
}

type TopicPath = {
  subject?: string;
  learning_domain?: string;
  learning_unit?: string;
  topic?: string;
};

function hasTopicPath(path: TopicPath) {
  return Boolean(path.subject && path.learning_domain && path.learning_unit && path.topic);
}

function firstPresent<T>(...values: Array<T | null | undefined | ''>): T | undefined {
  return values.find((value): value is T => value != null && value !== '');
}

function metaText(meta: Record<string, unknown>, key: string) {
  const value = meta[key];
  return value == null ? '' : String(value);
}

function isDraftQuestionPlaceholder(value: string): boolean {
  const text = value.trim();
  return text === '未命名中文題目' || text === '未命名題目';
}

function isDummyWordBankLabel(value: string): boolean {
  const text = value.trim();
  return /^[A-D]$/i.test(text) || /^Answer\s+[A-Z]$/i.test(text);
}

/** 單字-style bank words (T7/T8) may legally equal 問題文字. Long titles
 *  copied onto the word row are still treated as leakage. */
function isPlausibleWordBankWord(value: string): boolean {
  const text = value.trim();
  if (!text || isDummyWordBankLabel(text) || isDraftQuestionPlaceholder(text)) return false;
  const length = Array.from(text).filter((char) => !/\s/u.test(char)).length;
  return length >= 1 && length <= 4;
}

/** Drop dummy labels, draft placeholders, and leaked long question titles.
 *  Keep a short word even when it matches the question title (e.g. 單字「水」). */
function usableWordBankName(value: string | undefined, questionNames: string[]): string {
  const text = String(value || '').trim();
  if (!text || isDummyWordBankLabel(text) || isDraftQuestionPlaceholder(text)) return '';
  if (questionNames.some((name) => name.trim() && name.trim() === text)) {
    return isPlausibleWordBankWord(text) ? text : '';
  }
  return text;
}

function wordBankAnswerName(
  option: EditorialQuestionOption,
  input: EditorialQuestionInput
): string {
  const content = (option.content || '').trim();
  if (content) return content;
  const question = (input.questionContent || '').trim();
  if (isPlausibleWordBankWord(question)) return question;
  if (option.dictionaryWordId) return question || '詞語';
  return option.label;
}

function templateFromStored(value: unknown): EditorialQuestionTemplate {
  const text = String(value || '').toUpperCase();
  if (text in TEMPLATE_TO_TYPE_ID) return text as EditorialQuestionTemplate;
  return templateFromTypeId(text.replace(/^T/, '') || 1);
}

function parseQuestionTypeId(value: unknown): number | undefined {
  if (value == null || value === '') return undefined;
  const text = String(value).trim().toUpperCase();
  if (text in TEMPLATE_TO_TYPE_ID) {
    return TEMPLATE_TO_TYPE_ID[text as EditorialQuestionTemplate];
  }
  const num = Number(text.replace(/^T/, ''));
  if (!Number.isFinite(num) || num < 1 || num > 11) return undefined;
  return num;
}

/** Map get_full_info / list template fields onto T1–T11.
 *  Canonical question_type_id 2–11 wins. Type 1 is T1 unless section_meta
 *  savedTemplate says otherwise. Leftover mc_chinese must not force T7. */
function resolveEditorialTemplate(opts: {
  savedTemplate?: string;
  questionTypeId?: unknown;
}): EditorialQuestionTemplate {
  const typeId = parseQuestionTypeId(opts.questionTypeId);
  if (typeId != null && typeId !== 1) {
    return templateFromTypeId(typeId);
  }
  if (opts.savedTemplate) {
    return templateFromStored(opts.savedTemplate);
  }
  return templateFromTypeId(typeId || 1);
}

/** T7/T8 persist one resource_mc_answer_item so the client can attach a
 *  spelling user_log (score + recording) to answer_id. Canonical word data
 *  lives on the Chinese-word row (`mc_chinese[0]`); section_meta only keeps a
 *  1-item overlay (same shape for T7 and T8). */
function saveAllAnswerOptionLimit(template: EditorialQuestionTemplate): number | undefined {
  if (template === 'T7' || template === 'T8') return 1;
  if (template === 'T5') return 2;
  return undefined;
}

function buildSavedSectionMeta(input: EditorialQuestionInput, topicId: number) {
  const wordBankOptions = isChineseWordBankTemplate(input.template)
    ? (input.options || []).slice(0, input.template === 'T6' ? 4 : 1)
    : [];
  const wordBankOption =
    input.template === 'T6'
      ? wordBankOptions.find((option) => option.id === input.correctOptionId) || wordBankOptions[0]
      : wordBankOptions[0];
  return {
    ...input.sectionNotes,
    savedTemplate: input.template,
    savedQuestionTypeId: TEMPLATE_TO_TYPE_ID[input.template],
    savedSubject: input.subject,
    savedTaxonomy: input.taxonomy,
    savedLearningUnit: input.learningUnit,
    savedCategoryName: input.categoryName,
    savedTopic: input.topic,
    savedTopicId: topicId,
    savedQuestionContent: input.questionContent,
    savedImageHint: input.imageHint || '',
    savedAudioHint: input.audioHint || '',
    savedBackgroundHint: input.backgroundHint || '',
    savedVideoHint: input.videoHint || '',
    ...(isChineseWordBankTemplate(input.template)
      ? {
          wordBankOptions: wordBankOptions.map((option) => ({
            content: option.content || '',
            jyutping: option.jyutping || '',
            audioUrl: option.audioUrl?.startsWith('blob:') ? '' : option.audioUrl || '',
            dictionaryWordId: option.dictionaryWordId || '',
            dictionaryAudioId: option.dictionaryAudioId || '',
            manualWordAudio: Boolean(option.manualWordAudio)
          })),
          t6AudioText: wordBankOption?.content || '',
          t6Jyutping: wordBankOption?.jyutping || '',
          t6DictionaryWordId: wordBankOption?.dictionaryWordId || '',
          t6DictionaryAudioId: wordBankOption?.dictionaryAudioId || '',
          t6ManualWordAudio: wordBankOption?.manualWordAudio ? '1' : ''
        }
      : {})
  };
}

function topicPathFromFlatRows(rows: FlatTopicRow[] | undefined, topicId: number): TopicPath {
  if (!Array.isArray(rows)) return {};
  const byId = new Map(rows.map((row) => [String(row.id), row]));
  const chain: FlatTopicRow[] = [];
  const seen = new Set<string>();
  let row = byId.get(String(topicId));
  while (row && !seen.has(String(row.id))) {
    seen.add(String(row.id));
    chain.unshift(row);
    row = row.parent_id ? byId.get(String(row.parent_id)) : undefined;
  }
  return {
    subject: normalizeTopicSubject(chain[0]?.topic),
    learning_domain: chain[1]?.topic || '',
    learning_unit: chain[2]?.topic || '',
    topic: chain[3]?.topic || ''
  };
}

function mergeSavedWithInput(
  saved: EditorialQuestion,
  input: EditorialQuestionInput
): EditorialQuestion {
  return {
    ...saved,
    template: input.template || saved.template,
    level: input.level || saved.level,
    difficulty: input.difficulty || saved.difficulty,
    subject: saved.subject || input.subject,
    taxonomy: saved.taxonomy || input.taxonomy,
    learningUnit: saved.learningUnit || input.learningUnit,
    categoryName: saved.categoryName || input.categoryName,
    topic: saved.topic || input.topic,
    imageHint: input.imageHint || saved.imageHint || '',
    audioHint: input.audioHint || saved.audioHint || '',
    backgroundHint: input.backgroundHint || saved.backgroundHint || '',
    videoHint: input.videoHint || saved.videoHint || ''
  };
}

// ── Transformers (panel ↔ Henry wire) ────────────────────────────────────

function toSaveAllPayload(
  input: EditorialQuestionInput,
  bound: {
    id: number;
    res_id: number;
    answer_ids?: number[];
    topic_id: number;
    adapter_id?: number;
  }
): SaveAllPayload {
  const optionLimit = saveAllAnswerOptionLimit(input.template);
  const sourceOptions = optionLimit
    ? (input.options || []).slice(0, optionLimit)
    : input.options || [];
  const correctOptionId = sourceOptions.some((option) => option.id === input.correctOptionId)
    ? input.correctOptionId
    : sourceOptions[0]?.id;
  const wordBankOption =
    input.template === 'T6'
      ? sourceOptions.find((option) => option.id === correctOptionId) || sourceOptions[0]
      : sourceOptions[0];
  const resourceType = resourceTypeForQuestion(input);
  if (isChineseWordBankTemplate(input.template)) {
    return {
      res_id: bound.res_id,
      id: bound.id,
      adapter_id: bound.adapter_id,
      name: (input.questionContent || '').trim().slice(0, 60) || input.title || '未命名題目',
      question_name: input.questionContent || '',
      type: resourceType,
      difficulty: (input.difficulty as '1' | '2' | '3') || '1',
      target_grade: levelToTargetGrade(input.level),
      // Contract fix C3: this used to be hardcoded to 1, which persisted every
      // word-bank question as template 1 and broke list filtering by type.
      question_type_id: TEMPLATE_TO_TYPE_ID[input.template],
      topic_id: bound.topic_id,
      question_program: 'godot',
      // Contract fix C4: `confirm_type_change` / `confirm_type_value` were never
      // read by the backend — `save_all` reads `purge_chinese_words` +
      // `confirm_chinese_words_purge` instead. Removed rather than translated:
      // those two flags authorise DESTROYING a question's Chinese words, so they
      // must come from an explicit editor confirmation, never be auto-sent on
      // every save. Until that dialog exists, a chinese→math flip on a question
      // that still has words will be rejected by the backend, which is the safe
      // outcome. See the API contract mismatch report, C4.
      editorial_status: toEditorialStatus(input.status),
      section_meta: buildSavedSectionMeta(input, bound.topic_id),
      char_id: wordBankOption?.dictionaryWordId,
      generated_image_url: input.imageHint || undefined,
      // T7/T8 submit a single answer_id for spelling user_log. The spoken
      // word is the Chinese-word row (mc_chinese[0]), not extra MC answers.
      answers: sourceOptions.map((option) => {
        const name = wordBankAnswerName(option, input);
        return {
          id: resolveAnswerId(option, bound.answer_ids),
          name,
          is_correct: option.id === correctOptionId ? 'Y' : 'N',
          lang_data: [{ lang: 'zh', name }]
        };
      })
    };
  }
  const answers: SaveAllAnswer[] = sourceOptions.map((option) => {
    // Henry's save_all rejects with ANSWER_NAME_REQUIRED (result=0) if any
    // answer name is empty. Keep edited content; image-only answers fall back
    // to the option label so the backend still receives a non-empty name.
    const name = (option.content || '').trim() || option.label;
    return {
      id: resolveAnswerId(option, bound.answer_ids),
      name,
      is_correct: option.id === correctOptionId ? 'Y' : 'N',
      lang_data: [
        {
          lang: 'zh',
          name,
          image_url: option.imageUrl || undefined,
          image_prompt: option.imagePrompt || undefined
        }
      ]
    };
  });

  return {
    res_id: bound.res_id,
    id: bound.id,
    adapter_id: bound.adapter_id,
    // name mirrors the question text (問題文字). question_name carries the full
    // text so it round-trips via get_full_info → fromFullInfo → questionContent
    // → Godot preview. name is a length-safe slice for the resource.name column.
    name: (input.questionContent || '').trim().slice(0, 60) || input.title || '未命名題目',
    question_name: input.questionContent || '',
    type: resourceType,
    difficulty: (input.difficulty as '1' | '2' | '3') || '1',
    target_grade: levelToTargetGrade(input.level),
    question_type_id: TEMPLATE_TO_TYPE_ID[input.template],
    topic_id: bound.topic_id,
    question_program: 'godot',
    // Contract fix C4 — see the T6 branch above: the backend never read
    // confirm_type_*; the real keys authorise a destructive word purge and must
    // be user-confirmed, so they are not sent automatically.
    publish_status: input.status === 'approved' ? 'PUBLISHED' : 'DRAFT',
    editorial_status: toEditorialStatus(input.status),
    section_meta: buildSavedSectionMeta(input, bound.topic_id),
    generated_draft_question_id: input.generatedDraftQuestionId || undefined,
    generated_template_id: input.generatedTemplateId || undefined,
    generated_option_type: input.generatedOptionType || undefined,
    generated_option_layout: input.generatedOptionLayout || undefined,
    generated_explanation: input.generatedExplanation || input.explanation || undefined,
    generated_image_url: input.imageHint || undefined,
    answers
  };
}

async function fromFullInfo(
  response: FullInfoResponse,
  adapterId?: number | string
): Promise<EditorialQuestion> {
  // Single canonical question row lives in data.item[0]. The row carries
  // `item_type === 'mc_question'` as the type marker (Henry dropped the
  // separate `mc_question[]` duplicate on the read side).
  const q = response.item?.[0];
  if (!q) {
    throw new Error('get_full_info returned no item[0]');
  }
  const sectionMeta =
    typeof q.section_meta === 'string'
      ? safeParseJson<Record<string, unknown>>(q.section_meta) || {}
      : (q.section_meta as Record<string, unknown>) || {};

  const setting = (q.setting && q.setting[0]) || undefined;
  const answers = q.answer || [];
  const resourceItem = Array.isArray(response.resource_item)
    ? response.resource_item[0]
    : response.resource_item;
  const savedTemplate = firstPresent(
    metaText(sectionMeta, 'savedTemplate'),
    metaText(sectionMeta, 'savedQuestionTypeId')
  );
  const template = resolveEditorialTemplate({
    savedTemplate,
    questionTypeId: firstPresent(
      setting?.question_type_id,
      q.question_type_id,
      q.generated_template_id
    )
  });

  // Reverse-map the saved topic_id to its full taxonomy path so the 學習目標
  // cascade reopens populated (學科 → 範疇 → 學習單位 → 類別) instead of blank.
  // get_full_info gives us only setting.topic_id (+ setting.topic = the leaf
  // category name) and NO LONGER returns topic_type_list (finalized topic_type
  // API). Resolve the rest via topic_type/get, whose row carries the
  // denormalized subject / learning_domain / learning_unit. Fresh drafts with
  // no real topic (id null/empty/0) skip the lookup → fields stay blank.
  const rawTopicId = firstPresent(
    setting?.topic_id,
    q.topic_id,
    q.topic_type_id,
    metaText(sectionMeta, 'savedTopicId')
  );
  const settingTopicId =
    rawTopicId != null && String(rawTopicId) !== '' && String(rawTopicId) !== '0'
      ? Number(rawTopicId)
      : undefined;
  let topicPath: TopicPath = {
    subject: normalizeTopicSubject(metaText(sectionMeta, 'savedSubject')),
    learning_domain: metaText(sectionMeta, 'savedTaxonomy'),
    learning_unit: metaText(sectionMeta, 'savedLearningUnit'),
    topic: metaText(sectionMeta, 'savedCategoryName') || metaText(sectionMeta, 'savedTopic')
  };
  if (settingTopicId != null && Number.isFinite(settingTopicId)) {
    try {
      const row = await mainResourceClient.topicTypeFlatGet({ id: settingTopicId });
      if (row) {
        topicPath = {
          subject: normalizeTopicSubject(row.subject),
          learning_domain: row.learning_domain || '',
          learning_unit: row.learning_unit || '',
          topic: row.topic || ''
        };
      }
    } catch (topicErr) {
      // Leave the cascade blank if the lookup fails; topic_id still round-trips.
    }
    if (!hasTopicPath(topicPath)) {
      try {
        const res = await mainResourceClient.getTopicTypeFlatList();
        const derived = topicPathFromFlatRows(res.rows || res.list, settingTopicId);
        topicPath = {
          subject: topicPath.subject || derived.subject,
          learning_domain: topicPath.learning_domain || derived.learning_domain,
          learning_unit: topicPath.learning_unit || derived.learning_unit,
          topic: topicPath.topic || derived.topic
        };
      } catch (topicErr) {
        // Keep whatever topic_type/get gave us; topic_id still round-trips.
      }
    }
  }
  const resolvedTopicId = settingTopicId;

  const t6Word = isChineseWordBankTemplate(template)
    ? (q.mc_chinese ||
        q.chinese_words ||
        resourceItem?.mc_chinese ||
        resourceItem?.chinese_words ||
        response.mc_chinese ||
        response.chinese_words ||
        [])[0]
    : undefined;
  const pronunciationTemplate = template === 'T7' || template === 'T8';
  const pinnedAudioUrl = pronunciationTemplate ? playableWordAudioUrl(q.chinese_word_audio) : '';
  const wordAudios = t6Word?.audios || t6Word?.audio || [];
  const t6Audios = pronunciationTemplate
    ? wordAudios.filter((audio) => playableWordAudioUrl(audio))
    : wordAudios;
  const t6Audio =
    t6Audios.find(
      (audio) => audio.jyutping && t6Word?.jyutping && audio.jyutping === t6Word.jyutping
    ) || t6Audios[0];
  const t6AudioUrl = pinnedAudioUrl || String(t6Audio?.oss_url || t6Audio?.file || '');
  const savedWordBankOptions = Array.isArray(sectionMeta.wordBankOptions)
    ? sectionMeta.wordBankOptions.filter((option): option is Record<string, unknown> =>
        Boolean(option && typeof option === 'object')
      )
    : [];
  const savedImageHint = metaText(sectionMeta, 'savedImageHint');
  const savedQuestionAudio = metaText(sectionMeta, 'savedAudioHint');
  const savedBackgroundHint = metaText(sectionMeta, 'savedBackgroundHint');
  const savedVideoHint = metaText(sectionMeta, 'savedVideoHint');
  const questionSoundUrls = (q.sound || []).map((row) => row.oss_url || row.file || '');
  const savedQuestionAudioUrl = isAudioFileUrl(savedQuestionAudio) ? savedQuestionAudio : '';
  const questionAudio = isChineseWordBankTemplate(template)
    ? [savedQuestionAudioUrl, ...questionSoundUrls].find(
        (url) => isPlayableAssetUrl(url) && !sameAssetUrl(url, t6AudioUrl)
      ) || ''
    : savedQuestionAudioUrl || questionSoundUrls.find(isPlayableAssetUrl) || '';

  const optionSourceCount = (() => {
    if (template === 'T7' || template === 'T8') {
      return 1;
    }
    if (isChineseWordBankTemplate(template)) {
      return Math.max(answers.length, savedWordBankOptions.length);
    }
    return answers.length;
  })();

  const questionNames = [
    q.name || '',
    q.question_name || '',
    metaText(sectionMeta, 'savedQuestionContent')
  ];

  const options: EditorialQuestionOption[] = Array.from({ length: optionSourceCount }, (_, idx) => {
    const isWordBankOption = isChineseWordBankTemplate(template);
    const isLegacyWord = isWordBankOption && idx === 0;
    const savedWord = savedWordBankOptions[idx] || {};
    const answer = template === 'T7' || template === 'T8' ? answers[0] : answers[idx];
    const answerName = answer ? pickAnswerName(answer) : '';
    const fallbackWord = usableWordBankName(answerName, questionNames);
    return {
      id: OPTION_KEYS[idx] || (answer?.id != null ? String(answer.id) : String(idx)),
      label: OPTION_KEYS[idx]
        ? OPTION_KEYS[idx].toUpperCase()
        : answer?.id != null
        ? `#${answer.id}`
        : `#${idx + 1}`,
      content: isWordBankOption
        ? usableWordBankName(metaText(savedWord, 'content'), questionNames) ||
          (isLegacyWord
            ? usableWordBankName(metaText(sectionMeta, 't6AudioText'), questionNames) ||
              usableWordBankName(String(t6Word?.name || ''), questionNames)
            : '') ||
          fallbackWord
        : answerName,
      imageUrl: answer ? pickAnswerImage(answer) : undefined,
      audioUrl: isWordBankOption
        ? pronunciationTemplate
          ? pinnedAudioUrl ||
            [metaText(savedWord, 'audioUrl'), isLegacyWord ? t6AudioUrl : ''].find(
              isPlayableAssetUrl
            ) ||
            ''
          : metaText(savedWord, 'audioUrl') || (isLegacyWord ? t6AudioUrl : '')
        : answer
        ? pickAnswerSound(answer)
        : undefined,
      jyutping: isWordBankOption
        ? metaText(savedWord, 'jyutping') ||
          (isLegacyWord
            ? metaText(sectionMeta, 't6Jyutping') || String(t6Word?.jyutping || '')
            : '')
        : undefined,
      wordId: isLegacyWord && t6Word?.id != null ? Number(t6Word.id) : undefined,
      dictionaryWordId:
        isWordBankOption &&
        (metaText(savedWord, 'dictionaryWordId') ||
          (isLegacyWord ? metaText(sectionMeta, 't6DictionaryWordId') || t6Word?.char_id : ''))
          ? String(
              metaText(savedWord, 'dictionaryWordId') ||
                metaText(sectionMeta, 't6DictionaryWordId') ||
                t6Word?.char_id
            )
          : undefined,
      // Char-bank selection only (chinese_word_char_audio.id). Do not fall
      // back to t6Audio.id — that is resource_chinese_word_audio.id.
      dictionaryAudioId: isWordBankOption
        ? metaText(savedWord, 'dictionaryAudioId') ||
          (isLegacyWord ? metaText(sectionMeta, 't6DictionaryAudioId') : '') ||
          undefined
        : undefined,
      manualWordAudio: isWordBankOption
        ? savedWord.manualWordAudio === true ||
          (isLegacyWord && metaText(sectionMeta, 't6ManualWordAudio') === '1')
        : undefined,
      answerId: answer?.id != null ? Number(answer.id) : undefined,
      imagePrompt: answer ? pickAnswerImagePrompt(answer) : undefined
    };
  });

  // Older T8 resources may have only a dictionary clip, with no combined
  // resource word recording. Reuse the existing read API for that single char.
  const pronunciationOption = options[0];
  if (template === 'T8' && pronunciationOption?.dictionaryWordId && !pronunciationOption.audioUrl) {
    try {
      const character = await chineseDictionaryClient.get(pronunciationOption.dictionaryWordId);
      if (character?.word === pronunciationOption.content) {
        const reading = character.readings.cantonese.find(
          (item) =>
            isPlayableAssetUrl(item.audio_url || '') &&
            (!pronunciationOption.jyutping || item.code === pronunciationOption.jyutping)
        );
        pronunciationOption.audioUrl = reading?.audio_url || '';
      }
    } catch {
      // Optional audio must not prevent opening the saved question.
    }
  }

  const correctIdx = answers.findIndex((a) => a.is_correct === 'Y');
  const generatedTemplateId = q.generated_template_id
    ? templateFromTypeId(q.generated_template_id)
    : undefined;

  return {
    id: String(adapterId ?? q.resource_item_id),
    title: q.name || '',
    subject: topicPath.subject || '',
    level: targetGradeToLevel(firstPresent(setting?.target_grade, q.target_grade)),
    term: '上學期',
    topic: topicPath.topic || setting?.topic || '',
    releaseCode: adapterId ? `RES-${adapterId}` : '',
    taxonomy: topicPath.learning_domain || '',
    learningUnit: topicPath.learning_unit || '',
    categoryName: topicPath.topic || setting?.topic || '',
    evaluationIndicator: '',
    template,
    learningObjective: '',
    // question_name is the student-facing question text; name is the
    // internal display name ("dummy question").
    questionContent: q.question_name || q.name || metaText(sectionMeta, 'savedQuestionContent'),
    difficulty: String(firstPresent(setting?.difficulty, q.difficulty) || 1),
    options,
    // Derive from the options already built above so it stays consistent with
    // whatever id each option received — OPTION_KEYS[idx] overflows to undefined
    // past 4 answers and would silently point the correct marker at option 'a'.
    correctOptionId: options[correctIdx >= 0 ? correctIdx : 0]?.id || 'a',
    explanation: String(q.generated_explanation || ''),
    generatedDraftQuestionId: String(q.generated_draft_question_id || ''),
    generatedTemplateId,
    generatedOptionType: String(q.generated_option_type || ''),
    generatedOptionLayout: String(q.generated_option_layout || ''),
    generatedExplanation: String(q.generated_explanation || ''),
    imageHint:
      savedImageHint ||
      pickFirstFile(q.question_image) ||
      String(q.generated_image_url || '') ||
      '',
    audioHint: questionAudio,
    backgroundHint: savedBackgroundHint || pickFirstFile(q.background),
    videoHint: savedVideoHint || pickFirstFile(q.video),
    assetReady: false,
    stored: true,
    workflowNotes: '',
    sectionNotes: {
      basicInformation: sectionMeta.basicInformation || '',
      learningObjectives: sectionMeta.learningObjectives || '',
      templates: sectionMeta.templates || '',
      questionContent: sectionMeta.questionContent || '',
      options: sectionMeta.options || '',
      workflow: sectionMeta.workflow || ''
    },
    status: toEditorialStatus(q.editorial_status || 'draft'),
    updatedAt: new Date().toISOString(),
    // Henry binding fields. resource_item_id + res_id drive save_all;
    // answer_ids[idx] drives oss_signurl_answer per option.
    res_id: Number(q.res_id),
    resource_item_id: Number(q.resource_item_id),
    adapter_id: adapterId ? Number(adapterId) : null,
    answer_ids: answers.map((a) => Number(a.id)),
    topic_id: resolvedTopicId,
    chineseTip:
      template === 'T7' || template === 'T8'
        ? {
            chinese_word_audio: q.chinese_word_audio ?? null,
            chinese_word_char: q.chinese_word_char ?? null
          }
        : undefined
  } as EditorialQuestion;
}

function fromListRow(row: EditorialListRow): EditorialQuestion {
  const sectionMeta =
    typeof row.section_meta === 'string'
      ? safeParseJson<Record<string, unknown>>(row.section_meta)
      : row.section_meta;
  const savedTemplate = sectionMeta
    ? firstPresent(sectionMeta.savedTemplate, sectionMeta.savedQuestionTypeId)
    : undefined;

  return {
    // Panel-side id is the adapter_id (cross-app stable handle). get_full_info
    // accepts adapter_id; resource_item_id stays internal for save_all wiring.
    id: String(row.adapter_id),
    title: row.name,
    subject: normalizeTopicSubject(row.subject) || 'math',
    level: targetGradeToLevel(row.target_grade),
    term: '上學期',
    topic: row.topic_name || '',
    releaseCode: row.adapter_id ? `RES-${row.adapter_id}` : '',
    taxonomy: row.learning_domain || '',
    learningUnit: row.learning_unit || '',
    categoryName: row.topic_name || '',
    evaluationIndicator: '',
    template: resolveEditorialTemplate({
      savedTemplate,
      questionTypeId: row.question_type_id
    }),
    learningObjective: '',
    questionContent: row.name,
    difficulty: String(row.difficulty || 1),
    options: [],
    correctOptionId: 'a',
    explanation: '',
    imageHint: '',
    audioHint: '',
    assetReady: false,
    stored: true,
    workflowNotes: '',
    sectionNotes: {
      basicInformation: '',
      learningObjectives: '',
      templates: '',
      questionContent: '',
      options: '',
      workflow: ''
    },
    status: toEditorialStatus(row.editorial_status),
    // Recency for the dashboard's 最近題目動態 sort. Henry leaves
    // last_update_time null on never-edited rows (~half the list), so falling
    // back to now() floated those to the top as "most recent". Fall back to
    // create_time (the first update event) instead so genuinely-recent edits
    // surface; empty string only if the backend sent neither.
    updatedAt: row.last_update_time || row.create_time || '',
    res_id: row.res_id,
    resource_item_id: row.item_id,
    adapter_id: row.adapter_id,
    topic_id: row.topic_id || undefined
  } as EditorialQuestion;
}

function pickAnswerName(answer: FullInfoAnswer): string {
  const langs = [...(answer.lang || []), ...(answer.lang_data || [])];
  const zh = langs.find((l) => l.lang === 'zh');
  return (zh && zh.name) || answer.name || '';
}

function pickAnswerImage(answer: FullInfoAnswer): string | undefined {
  // Verified 2026-05-28: per-option image OSS URL lands on
  // answer.lang[i].file (full https URL) after the oss_callback_answer
  // hook fires. Chinese save_all can echo lang_data[i].image_url instead.
  const langs = [...(answer.lang || []), ...(answer.lang_data || [])];
  const zh = langs.find((l) => l.lang === 'zh');
  if (zh && (zh.file || zh.oss_url || zh.image_url)) {
    return zh.file || zh.oss_url || zh.image_url || undefined;
  }
  return answer.file || answer.oss_url || answer.image_url || undefined;
}

function pickAnswerSound(answer: FullInfoAnswer): string | undefined {
  if (!Array.isArray(answer.sound) || answer.sound.length === 0) {
    return undefined;
  }
  const cantonese = answer.sound.find((row) => String(row.vo_lang) === '1');
  return (cantonese && cantonese.file) || answer.sound[0].file || undefined;
}

function pickAnswerImagePrompt(answer: FullInfoAnswer): string | undefined {
  const langs = [...(answer.lang || []), ...(answer.lang_data || [])];
  const zh = langs.find((l) => l.lang === 'zh');
  return (zh && zh.image_prompt) || answer.image_prompt || undefined;
}

// Asset rows (question_image / background / video / sound) carry a full OSS
// URL in `.oss_url` or `.file`. Prefer the zh row for image/background.
function pickFirstFile(
  rows: Array<{ lang?: string; file?: string | null; oss_url?: string | null }> | undefined
): string {
  if (!Array.isArray(rows) || rows.length === 0) {
    return '';
  }
  const zh = rows.find((r) => r.lang === 'zh');
  return (zh && (zh.oss_url || zh.file)) || rows[0].oss_url || rows[0].file || '';
}

function sameAssetUrl(left: string, right: string) {
  return Boolean(left && right && left.split(/[?#]/)[0] === right.split(/[?#]/)[0]);
}

function isPlayableAssetUrl(value: string) {
  return /^(https?:|blob:|data:audio\/)/i.test(value);
}

function playableWordAudioUrl(audio?: ChineseWordAudio | null): string {
  if (
    !audio ||
    ['INVALID', 'ERROR', 'NEW', 'PROCESSING'].includes(String(audio.status).toUpperCase())
  )
    return '';
  const url = String(audio.oss_url || audio.file || '').trim();
  return isPlayableAssetUrl(url) ? url : '';
}

function isAudioFileUrl(value: string) {
  return (
    isPlayableAssetUrl(value) &&
    (/[?&]v=\d+(?:&|$)/.test(value) || /\.(mp3|mpeg|wav|ogg|m4a|aac|webm)(?:[?#]|$)/i.test(value))
  );
}

function safeParseJson<T>(text: string): T | null {
  try {
    return JSON.parse(text) as T;
  } catch {
    return null;
  }
}

function uniqueTexts(values: string[], limit: number) {
  const seen = new Set<string>();
  const output: string[] = [];

  values.forEach((value) => {
    const text = String(value || '').trim();
    if (!text || seen.has(text)) {
      return;
    }
    seen.add(text);
    output.push(text);
  });

  return output.slice(0, limit);
}

function pickT6AudioTextFromSectionMeta(
  sectionMeta: Record<string, unknown> | string | null | undefined
) {
  const meta =
    typeof sectionMeta === 'string'
      ? safeParseJson<Record<string, unknown>>(sectionMeta)
      : sectionMeta;
  return String(meta?.t6AudioText || '').trim();
}

function pickT6JyutpingFromSectionMeta(
  sectionMeta: Record<string, unknown> | string | null | undefined
) {
  const meta =
    typeof sectionMeta === 'string'
      ? safeParseJson<Record<string, unknown>>(sectionMeta)
      : sectionMeta;
  return String(meta?.t6Jyutping || '').trim();
}

function uniqueT6AudioWordItems(values: Array<T6AudioWordItem | null | undefined>, limit: number) {
  const seen = new Set<string>();
  const output: T6AudioWordItem[] = [];

  values.forEach((value) => {
    const audioText = String(value?.audioText || '').trim();
    if (!audioText) {
      return;
    }
    const key = [
      audioText,
      String(value?.jyutping || '').trim(),
      String(value?.audioUrl || '').trim(),
      String(value?.wordId || '').trim()
    ].join('\u0000');
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    output.push({
      audioText,
      jyutping: String(value?.jyutping || '').trim(),
      audioUrl: String(value?.audioUrl || '').trim(),
      wordId: value?.wordId
    });
  });

  return output.slice(0, limit);
}

// ── Topic resolution ────────────────────────────────────────────────────

// Henry's save_all requires topic_id (number). Panel state currently carries
// the taxonomy as free text (subject/taxonomy/learningUnit/categoryName);
// resolving these to a topic_type_id is a Stream Y follow-up.
//
// For now, the Henry client expects callers to set EditorialQuestion.topic_id
// directly. The taxonomy cascade UI that would populate it is a Stream Y
// follow-up and isn't wired yet, so a hard throw here would block ALL saves.
// Henry's own happy flow (add_math_question + the finalized save_all_new)
// defaults topic_id to 1, so we mirror that: use the question's topic_id when
// present, otherwise fall back to 1 with a warning. Once the cascade UI lands,
// this fallback becomes dead code (callers always pass a real topic_id).
const DEFAULT_TOPIC_ID = 1;
function resolveTopicId(input: EditorialQuestionInput): number {
  const candidate = (input as EditorialQuestionInput & { topic_id?: number }).topic_id;
  if (typeof candidate === 'number' && candidate > 0) {
    return candidate;
  }
  // eslint-disable-next-line no-console
  console.warn(
    `[editorialQuestion] no topic_id on save — defaulting to ${DEFAULT_TOPIC_ID}. Wire the taxonomy cascade UI to set a real topic_id.`
  );
  return DEFAULT_TOPIC_ID;
}

// ── Henry client ────────────────────────────────────────────────────────

async function listPagedHenry(
  params: EditorialQuestionListParams = {}
): Promise<EditorialQuestionListResult> {
  const page = params.page ?? 0;
  const limit = params.limit ?? 10;
  const response = await mainResourceClient.getEditorialList(
    {
      keyword: params.keyword || undefined,
      subject: params.subject || undefined,
      scope: params.scope || undefined,
      unit: params.unit || undefined,
      // topic_id is already numeric (resolved page-side via findTopicTypeId);
      // no string→number conversion needed here. Falls back to undefined when
      // the cascade is incomplete, so Henry ignores it.
      topic_id:
        typeof params.topic_id === 'number' && params.topic_id > 0 ? params.topic_id : undefined,
      // Henry's get_editorial_list filters template by the numeric
      // question_type_id (1-6), not the panel's T1-T6 enum.
      template: params.template ? TEMPLATE_TO_TYPE_ID[params.template] : undefined,
      // Contract fix H2: get_editorial_list defaults res_type to 'mc_math' when
      // omitted, which hides every Chinese (T6) row from the bank list. The
      // agreed contract is that bank clients always send 'all'.
      res_type: 'all',
      editorial_status: params.status || undefined
    } as EditorialListFilters,
    {
      limit,
      offset: page * limit
    }
  );

  return {
    rows: (response?.rows || []).map(fromListRow),
    total: response?.total || 0
  };
}

export const editorialQuestionHenryClient = {
  async list() {
    const result = await listPagedHenry({ limit: 9999 });
    return result.rows;
  },

  listPaged: listPagedHenry,

  async listT6AudioWordItems(limit = 20): Promise<T6AudioWordItem[]> {
    const cappedLimit = Math.max(1, Math.min(limit, 50));
    const control = { limit: cappedLimit, offset: 0 };
    // Contract fix H2: without res_type the backend defaults to 'mc_math',
    // so Chinese word-bank (mc_chinese) rows would be filtered out.
    const [t7Response, t8Response] = await Promise.all([
      mainResourceClient.getEditorialList(
        { template: TEMPLATE_TO_TYPE_ID.T7, res_type: 'all' } as EditorialListFilters,
        control
      ),
      mainResourceClient.getEditorialList(
        { template: TEMPLATE_TO_TYPE_ID.T8, res_type: 'all' } as EditorialListFilters,
        control
      )
    ]);
    const rows = [...(t7Response?.rows || []), ...(t8Response?.rows || [])];
    const fromList: T6AudioWordItem[] = [];

    // ponytail: keep these list requests; rows without section_meta are not suggestions.
    rows.forEach((row) => {
      const text = pickT6AudioTextFromSectionMeta(row.section_meta);
      if (text) {
        fromList.push({
          audioText: text,
          jyutping: pickT6JyutpingFromSectionMeta(row.section_meta),
          audioUrl: '',
          wordId: row.item_id
        });
      }
    });

    return uniqueT6AudioWordItems(fromList, cappedLimit);
  },

  async listT6AudioTextSuggestions(limit = 20): Promise<string[]> {
    const items = await this.listT6AudioWordItems(limit);
    return uniqueTexts(
      items.map((item) => item.audioText),
      limit
    );
  },

  async get(id: string) {
    const response = await mainResourceClient.getFullInfo({ id: Number(id) });
    return fromFullInfo(response, id);
  },

  /** Create a blank draft so subsequent file uploads have a real material_id
   *  (resource_item_id) and answer_ids to attach to before the editor opens.
   *
   *  After Henry commit f970d39a:
   *   1. `add_math_question` returns only `{ adapter_id }`.
   *   2. `get_full_info({ id: adapter_id })` returns the full shell including
   *      res_id, resource_item.id, and the 4 auto-created answer rows with
   *      their ids — those are what subsequent oss_signurl_answer calls
   *      attach to. */
  async createDraft(template: EditorialQuestionTemplate = 'T1') {
    // Contract fix C2: `add_math_question` routes on `type`.
    // Chinese word-bank templates (T6/T7/T8) must be created as `mc_chinese`
    // (→ `addMcChineseBundle`). `name` must be non-empty for the Chinese
    // bundle, so send an explicit placeholder rather than relying on the
    // math default.
    const isChinese = isChineseWordBankTemplate(template);
    const payload = {
      difficulty: '1',
      target_grade: 'k2',
      question_type_id: TEMPLATE_TO_TYPE_ID[template],
      question_program: 'godot',
      type: isChinese ? ('mc_chinese' as const) : ('mc_math' as const),
      // `name` is required by evi-resource-service `hotfix/question-set-code-
      // standards`, whose controller-level `checkingList` (Main_resource.php:
      // 48-62) declares `'add_math_question' => ['name']`. That requirement is
      // already satisfied for every template — `main_resource.ts::addResource`
      // has defaulted `name: '未命名題目'` ahead of `...payload` since 3dbce6d5.
      // Setting it explicitly here only gives Chinese drafts their own
      // placeholder instead of the generic math one; it is not a bug fix.
      name: isChinese ? '未命名中文題目' : '未命名題目'
    } as const;
    let created: AddResourceResponse;
    try {
      created = await mainResourceClient.addResource(payload);
    } catch (error) {
      if (!(error instanceof MainResourceError) || error.code !== MainResourceResultCode.Failed) {
        throw error;
      }
      created = await mainResourceClient.addResource(payload);
    }
    const full = await mainResourceClient.getFullInfo({ id: created.adapter_id });
    const draft = await fromFullInfo(full, created.adapter_id);
    return {
      ...draft,
      options: draft.options.map((option) => ({ ...option, content: '' }))
    };
  },

  async save(
    input: EditorialQuestionInput,
    id?: string,
    _options?: SaveOptions
  ): Promise<EditorialQuestion> {
    const bound = await this._resolveBinding(input, id);
    // Word-bank payload intentionally omits chinese_word_audio_id so save_all
    // can IMM-combine from char_map when unpinned (never pin dictionaryAudioId).
    const payload = toSaveAllPayload(input, bound);
    const saveResult = await mainResourceClient.saveAll(payload);
    // Re-fetch by adapter_id (panel id), not resource_item_id — get_full_info
    // keys on the adapter handle.
    const adapterId =
      id ||
      (input as EditorialQuestionInput & { adapter_id?: number }).adapter_id ||
      bound.adapter_id;
    const full = await mainResourceClient.getFullInfo({ id: Number(adapterId ?? bound.id) });
    const merged = mergeSavedWithInput(await fromFullInfo(full, adapterId), input);
    if (!isChineseWordBankTemplate(input.template)) {
      return merged;
    }
    const audioIdRaw = saveResult?.chinese_word_audio_id;
    const audioId = audioIdRaw == null || audioIdRaw === '' ? null : Number(audioIdRaw);
    return {
      ...merged,
      chinese_word_audio_id: Number.isFinite(audioId as number) ? (audioId as number) : null,
      chinese_word_audio_combine_reason:
        saveResult?.chinese_word_audio_combine_reason != null
          ? String(saveResult.chinese_word_audio_combine_reason)
          : null
    };
  },

  /** Editorial status changes go through save_all in Henry's contract — there
   *  is no separate transition endpoint. Surface the legal-transitions table
   *  client-side so the editor can disable illegal dropdown choices. */
  async transition(id: string, status: EditorialQuestionStatus) {
    const full = await mainResourceClient.getFullInfo({ id: Number(id) });
    const current = await fromFullInfo(full, id);
    const legal = EDITORIAL_TRANSITIONS[current.status] || [];
    if (!legal.includes(status as EditorialStatus)) {
      throw new MainResourceError(
        -1,
        `Editorial transition ${
          current.status
        } → ${status} is not allowed. Legal next: ${legal.join(', ')}.`
      );
    }
    return this.save({ ...toInputFromQuestion(current), status } as EditorialQuestionInput, id);
  },

  async remove(id: string) {
    await this.transition(id, 'archived');
  },

  /** Internal — figures out (id, res_id, answer_ids, topic_id) for save_all. */
  async _resolveBinding(input: EditorialQuestionInput, id?: string) {
    const bound = input as EditorialQuestionInput & {
      res_id?: number;
      resource_item_id?: number;
      answer_ids?: number[];
    };
    const topicId = resolveTopicId(input);

    if (id) {
      // Updating an existing question; expect the editor to pass res_id and
      // answer_ids via input (panel keeps them on `selected` after
      // createDraft / get).
      const resId = bound.res_id;
      const answerIds = bound.answer_ids;
      const residentItemId = bound.resource_item_id;
      if (!resId || !residentItemId) {
        // Fall back to a server lookup if the editor lost the cached ids.
        // `id` here is the adapter_id (panel id); get_full_info keys on it.
        const full = await mainResourceClient.getFullInfo({ id: Number(id) });
        return { ...bindingFromFullInfo(full), topic_id: topicId, adapter_id: Number(id) };
      }
      return {
        id: Number(residentItemId),
        res_id: Number(resId),
        answer_ids: answerIds || [],
        topic_id: topicId,
        adapter_id: Number(id)
      };
    }

    // First save of a brand-new question — create draft inline if the panel
    // hasn't already done so on /new mount. add_math_question only returns
    // adapter_id; need a follow-up get_full_info to recover res_id +
    // resource_item.id + answer_ids.
    const created = await mainResourceClient.addResource({
      type: resourceTypeForQuestion(input),
      name: (input.questionContent || '').trim().slice(0, 60) || input.title || '未命名題目',
      difficulty: (input.difficulty as '1' | '2' | '3') || '1',
      target_grade: levelToTargetGrade(input.level),
      question_type_id: TEMPLATE_TO_TYPE_ID[input.template],
      topic_id: topicId,
      question_program: 'godot',
      publish_status: input.status === 'approved' ? 'PUBLISHED' : 'DRAFT'
    });
    const full = await mainResourceClient.getFullInfo({ id: created.adapter_id });
    return { ...bindingFromFullInfo(full), topic_id: topicId, adapter_id: created.adapter_id };
  }
};

// get_full_info returns a single canonical shape: data.item[0] (verified
// 2026-06-04 after Henry dropped the duplicate `mc_question[]` array).
// The row carries `item_type === 'mc_question'` as the type marker, plus
// all the question-level fields (resource_item_id, res_id, answer[],
// setting[], etc.). For freshly-created drafts `resource_item_id` may be
// absent and `id` carries the resource_item id instead — fall back to
// `id` so the binding still resolves on the first save.
function bindingFromFullInfo(full: FullInfoResponse): {
  id: number;
  res_id: number;
  answer_ids: number[];
} {
  const mc = full.item?.[0];
  if (!mc) {
    // Fail fast instead of shipping NaN ids into save_all (a missing/permission-
    // denied shape). Mirrors fromFullInfo's 'returned no item[0]' guard.
    throw new MainResourceError(
      -1,
      'get_full_info returned no item[0] — cannot resolve save binding.'
    );
  }
  const node = (mc || {}) as { resource_item_id?: string | number; id?: string | number };
  const residentItemId = node.resource_item_id ?? node.id;
  return {
    id: Number(residentItemId),
    res_id: Number(mc?.res_id),
    answer_ids: (mc?.answer || []).map((a) => Number(a.id))
  };
}

function toInputFromQuestion(question: EditorialQuestion): EditorialQuestionInput {
  // Mirror the panel-side `toInput` helper without importing the editor
  // module (would create a cycle). Used by transition() to repost via save.
  const rest: Partial<EditorialQuestion> = { ...question };
  delete rest.id;
  delete rest.updatedAt;
  return rest as EditorialQuestionInput;
}

// Env-var gate. Defaults to Henry's real backend now that Stream E is wired.
// All env-cmd builds (uat / staging / production) route to Henry via
// REACT_APP_API_URL_RESOURCE. Opt-in to the json-server-backed mock client
// for local dev only by setting REACT_APP_EDITORIAL_QUESTION_CLIENT=mock
// (typically in .env.local) AND REACT_APP_EDITORIAL_QUESTION_MOCK_API_URL.
export const editorialQuestionClient =
  process.env.REACT_APP_EDITORIAL_QUESTION_CLIENT === 'mock'
    ? editorialQuestionMockClient
    : editorialQuestionHenryClient;
// process.env.REACT_APP_EDITORIAL_QUESTION_CLIENT === 'henry'
// ? editorialQuestionHenryClient
// : editorialQuestionMockClient;
